import {Injectable, inject} from '@angular/core'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {Store} from '@ngrx/store'
import {forkJoin, from, of} from 'rxjs'
import {exhaustMap, map, switchMap} from 'rxjs/operators'
import {catchSwitchMapError} from '@common/utils/owner-rxjs-operators.util'
import {Action, Action as ActionModel} from '@models/action'
import {UveLogKey} from '@models/constants/log-key'
import {Distributor} from '@models/distributor'
import {ActionResponseOperation} from '@models/enums/action-response.enum'
import {Order} from '@models/order/order'
import {Outlet} from '@models/outlet'
import {ApiService} from '@service/api.service'
import {LoggingService} from '@service/logging.service'
import {NewOrderService} from '@service/orders/new/new-order.service'
import {OrderService} from '@service/orders/order/order.service'
import {OutletDetailService} from '@service/outlet-detail.service'
import {ReactiveFieldsDataService} from '@service/reactive-fields/reactive-fields-data.service'
import {
  OrdersActionsTypes,
  clearOrderResponseResult,
  getOrdersListResponse,
  resetCartProducts,
  saveOrderResponseResult,
} from './orders.actions'
import {TotalizationsService} from '@service/totalizations/totalizations.service'

@Injectable()
export class OrdersEffects {
  readonly #actions$ = inject(Actions)
  readonly #store = inject(Store)
  readonly #orderService = inject(OrderService)
  readonly #apiService = inject(ApiService)
  readonly #newOrderService = inject(NewOrderService)
  readonly #loggingService = inject(LoggingService)
  readonly #outletDetailService = inject(OutletDetailService)
  readonly #reactiveFieldsDataService = inject(ReactiveFieldsDataService)
  readonly #totalizationService = inject(TotalizationsService)

  saveOrder$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(OrdersActionsTypes.SAVE_ORDER_REQUEST),
      exhaustMap(
        ({
          order,
          outlet,
          distributor,
          deliveryDate,
          email,
          isTransfer,
          action,
          activityId,
          orderState,
        }: {
          order: Order
          outlet: Outlet
          distributor: Distributor
          deliveryDate: Date
          email: string
          isTransfer: boolean
          action: ActionModel
          activityId: string
          orderState: string
        }) =>
          forkJoin({
            action: of(action),
            order: this.#newOrderService.saveOrder(order, outlet, distributor, deliveryDate, email, isTransfer),
            outlet: of(outlet),
            activityId: of(activityId),
            orderState: of(orderState),
          })
      ),
      switchMap(
        ({
          action,
          order,
          outlet,
          activityId,
          orderState,
        }: {
          action: Action
          order: any
          outlet: Outlet
          activityId: string
          orderState: string
        }) => {
          this.#apiService.deleteItemLocalInfo('DRAFT_ORDER_' + outlet.id + '_' + action?.id)
          this.#store.dispatch(resetCartProducts())
          this.#newOrderService.clearOrder()
          this.#reactiveFieldsDataService.clean()
          this.#outletDetailService.data.loadedOutletId = ''
          this.#outletDetailService.loadingOutlet = ''
          this.#totalizationService.hasSelectedProducts = false

          if (order.products.length === 0) {
            this.#loggingService.debug(UveLogKey.Debug.OrderNoProducts, {
              value: `ordId:${order.id} outId:${outlet.id} actId:${activityId}`,
            })
          }

          const statusChange$ = orderState ? from(this.#orderService.changeOrderStatus(order, orderState)) : of(null)
          return statusChange$.pipe(switchMap(() => forkJoin([of(order), of(activityId), of(action)])))
        }
      ),
      switchMap(([order, activityId, action]: [Order, string, Action]) =>
        of(
          saveOrderResponseResult({
            operation: ActionResponseOperation.SUCCESS,
            data: {outletId: order.outletMainId?.toString(), activityId, action, orderId: order.id},
            error: null,
          })
        )
      ),
      catchSwitchMapError(error =>
        saveOrderResponseResult({operation: ActionResponseOperation.ERROR, data: null, error})
      )
    )
  )

  saveOrderResponseResult$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(OrdersActionsTypes.SAVE_ORDER_RESPONSE_RESULT),
      switchMap(() => of(clearOrderResponseResult()))
    )
  )

  orderList$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(OrdersActionsTypes.GET_ORDERS_REQUEST),
      switchMap(({outletMainId}) => this.#orderService.getList(outletMainId)),
      map((orders: Order[]) => getOrdersListResponse({orders}))
    )
  )
}
